import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { Card } from '../../components/card';
import { Symbol } from '../../components/symbol';
import { SkeletonRows } from '../../components/skeleton';
import { Toggle } from '../../components/toggle';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useTableSearch } from '../../components/table-search';
import { useConfirm } from '../../components/confirm-dialog';
import { useAuthStore } from '../../store/auth-store';
import {
  CrudDrawer,
  TextField,
  NumberField,
  CheckField,
  DeleteButton,
  extractErr,
} from '../../components/crud-drawer';

interface TaxRule {
  id: string;
  name: string;
  taxPct: string;
  hsnCodePattern: string | null;
  isActive: boolean;
}

const EMPTY = { name: '', taxPct: '', hsnCodePattern: '', isActive: true };

export function TaxRulesTab() {
  const canWrite = useAuthStore((s) => s.hasScope)('catalog.taxrule.write');

  const [rows, setRows] = useState<TaxRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<TaxRule | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const { confirm } = useConfirm();
  // Client-side: the API returns every rule. Matches each column shown.
  const { query, setQuery, filtered } = useTableSearch(rows, (r) => [
    r.name, `${Number(r.taxPct).toFixed(2)}%`, r.taxPct, r.hsnCodePattern, r.isActive ? 'Active' : 'Inactive',
  ]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get<TaxRule[]>('/admin/tax-rules');
      setRows(data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const openCreate = () => { setEditing(null); setCreating(true); setForm(EMPTY); };

  const openEdit = (row: TaxRule) => {
    setCreating(false); setEditing(row);
    setForm({
      name: row.name,
      taxPct: String(row.taxPct),
      hsnCodePattern: row.hsnCodePattern ?? '',
      isActive: row.isActive,
    });
  };

  const close = () => { setEditing(null); setCreating(false); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const pct = Number(form.taxPct);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error('Tax percent must be between 0 and 100'); return;
    }
    const body = {
      name: form.name.trim(),
      taxPct: pct,
      hsnCodePattern: form.hsnCodePattern.trim() || null,
      isActive: form.isActive,
    };
    setBusy(true);
    try {
      if (creating) {
        await api.post('/admin/tax-rules', body);
        toast.success('Tax rule created');
      } else if (editing) {
        await api.put(`/admin/tax-rules/${editing.id}`, body);
        toast.success('Tax rule updated');
      }
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    const ok = await confirm({
      title: `Delete "${editing.name}"?`,
      description: 'This can\'t be undone. Products already pointing at this rule would lose their tax rate, so deactivating is safer.',
      confirmLabel: 'Delete tax rule',
      tone: 'danger',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/admin/tax-rules/${editing.id}`);
      toast.success('Tax rule deleted');
      close();
      await load();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: TaxRule, next: boolean) => {
    // Switching off hides it at once, so ask first; switching back on is
    // harmless and stays one click.
    if (!next) {
      const ok = await confirm({
        title: `Deactivate "${row.name}"?`,
        description: 'It can no longer be assigned to new products; products already using it keep their rate. You can switch it back on any time.',
        confirmLabel: 'Deactivate rule',
        tone: 'danger',
      });
      if (!ok) return;
    }
    setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: next } : r)));
    try {
      await api.put(`/admin/tax-rules/${row.id}`, { isActive: next });
    } catch (err) {
      toast.error(extractErr(err));
      setRows((cur) => cur.map((r) => (r.id === row.id ? { ...r, isActive: !next } : r)));
    }
  };

  return (
    <>
      <Card padding="none">
        <div className="flex items-center justify-between border-b border-surface-line px-5 py-4">
          <div>
            <h2 className="text-[16px] font-semibold text-ink-900">Tax rules</h2>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              GST rates a product can be assigned. The HSN pattern lets a rule be matched
              automatically when a seller enters an HSN code.
            </p>
          </div>
          {canWrite && (
            <button
              type="button"
              onClick={openCreate}
              className="btn btn-primary btn-sm shrink-0"
            >
              <Symbol name="add" size={16} /> Add tax rule
            </button>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="border-b border-surface-line px-5 py-3">
            <TableSearch
              value={query}
              onChange={setQuery}
              placeholder="Search by name, rate, HSN pattern or status"
              resultCount={filtered.length}
            />
          </div>
        )}

        {loading ? (
          <SkeletonRows cols={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="percent"
            title="No tax rules yet"
            body="GST rates you add here can be assigned to products."
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for "${query.trim()}"`}
            body="Try a rule name, a rate like 12, an HSN pattern, or Active / Inactive."
            action={
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setQuery('')}>
                Clear search
              </button>
            }
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th className="!text-right">Rate</th>
                <th className="hidden sm:table-cell">HSN pattern</th>
                <th className="!text-right">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className={r.isActive ? '' : 'bg-surface-sunk'}>
                  <td className="font-medium text-ink-900">{r.name}</td>
                  <td className="text-right font-tabular font-semibold text-ink-900">
                    {Number(r.taxPct).toFixed(2)}%
                  </td>
                  <td className="hidden sku text-ink-500 sm:table-cell">
                    {r.hsnCodePattern ?? '—'}
                  </td>
                  <td>
                    <div className="flex justify-end">
                      <Toggle
                        checked={r.isActive}
                        disabled={!canWrite}
                        aria-label={`${r.isActive ? 'Deactivate' : 'Activate'} ${r.name}`}
                        onChange={(v) => void toggleActive(r, v)}
                      />
                    </div>
                  </td>
                  <td className="text-right">
                    {canWrite && (
                      <button
                        type="button"
                        onClick={() => openEdit(r)}
                        className="btn btn-ghost btn-sm"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </Card>

      <CrudDrawer
        open={creating || editing !== null}
        title={creating ? 'New tax rule' : `Edit ${editing?.name ?? ''}`}
        busy={busy}
        onClose={close}
        onSubmit={() => void save()}
        secondaryAction={editing ? <DeleteButton onClick={() => void remove()} disabled={busy} /> : undefined}
      >
        <TextField
          label="Name"
          value={form.name}
          onChange={(v) => setForm((f) => ({ ...f, name: v }))}
          placeholder="GST 5% — apparel under ₹1000"
          required
        />
        <NumberField
          label="Tax percent"
          value={form.taxPct}
          onChange={(v) => setForm((f) => ({ ...f, taxPct: v }))}
          placeholder="5"
          step="0.01"
          min={0}
          max={100}
          required
          hint="Applied to the product's taxable value."
        />
        <TextField
          label="HSN pattern"
          value={form.hsnCodePattern}
          onChange={(v) => setForm((f) => ({ ...f, hsnCodePattern: v }))}
          placeholder="6104%"
          mono
          hint="Optional. A prefix like 6104% matches every HSN code starting with 6104."
        />
        <CheckField
          label="Active"
          hint="Inactive rules can't be assigned to new products; products already using one keep their rate."
          checked={form.isActive}
          onChange={(v) => setForm((f) => ({ ...f, isActive: v }))}
        />
      </CrudDrawer>
    </>
  );
}
